import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components";

function formatBarentzAPIErrorMessage(
  raw: string | null | undefined
): { code?: string; description?: string } | null {
  if (!raw?.trim()) return null;

  const trimmed = raw.trim();

  try {
    const rawObject = JSON.parse(trimmed) as Record<string, unknown>;
    if (
      rawObject &&
      typeof rawObject === "object" &&
      ("Code" in rawObject || "Description" in rawObject)
    ) {
      const code = String(rawObject.Code ?? "").trim() || undefined;
      const description =
        String(rawObject.Description ?? "").trim() || undefined;
      if (code && description) return { code, description };
      if (description) return { description };
      if (code) return { code, description: trimmed };
    }
  } catch {
    // not JSON – fallback
  }

  const code = trimmed.match(/(?:^\{|,)\s*'Code':\s*'([^']*)'/)?.[1]?.trim();

  let description: string | undefined;
  const descIdx = trimmed.indexOf("'Description':");
  if (descIdx !== -1) {
    let rest = trimmed.slice(descIdx + "'Description':".length).trimStart();
    const nextKey = rest.search(/,\s*'[^']+'\s*:/);
    if (nextKey !== -1) rest = rest.slice(0, nextKey);
    rest = rest.replace(/\s*\}$/, "").trim();
    const q = rest[0];
    if (q === '"' || q === "'") {
      const end = rest.lastIndexOf(q);
      description =
        end > 0 ? rest.slice(1, end).trim() || undefined : undefined;
    }
  }

  if (code && description) return { code, description };
  if (description) return { description };
  if (code) return { code, description: trimmed };
  return { description: trimmed };
}

export function BarentzAPIErrorMessage({
  raw,
  emptyText,
}: {
  raw: string | null | undefined;
  emptyText?: string;
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const formatted = formatBarentzAPIErrorMessage(raw);

  if (formatted) {
    const { code, description } = formatted;
    return (
      <>
        {raw === "-" || raw === "" ? (
          <div>{raw}</div>
        ) : (
          <>
            {code && (
              <div
                style={{ overflowWrap: "anywhere", wordBreak: "break-word" }}
              >
                <span style={{ fontWeight: theme.fontWeights.large }}>
                  {t("Code")}
                </span>
                {": "}
                {code}
              </div>
            )}
            {description && (
              <div
                style={{ overflowWrap: "anywhere", wordBreak: "break-word" }}
              >
                <span style={{ fontWeight: theme.fontWeights.large }}>
                  {t("Description")}
                </span>
                {": "}
                {description}
              </div>
            )}
          </>
        )}
      </>
    );
  }

  if (raw?.trim()) {
    return (
      <div style={{ overflowWrap: "anywhere", wordBreak: "break-word" }}>
        {raw}
      </div>
    );
  }
  return <div>{emptyText ?? t("No error details available")}</div>;
}
